import React, { useEffect } from 'react';
import { Box, Grid, TextField, Typography } from '@material-ui/core';

import CancelIcon from '@material-ui/icons/Cancel';
import { Search } from '@material-ui/icons';
import { IDateInput, IFilter, useFiltersContext } from '../../../../../contexts/filter/FiltersProvider';
import DateFnsUtils from '@date-io/date-fns';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { useTranslation } from 'react-i18next';
import tPaths from '../../../../../utils/tPaths';

interface ISearchContainer extends ITextFieldInput {
  inputType: string;
  tableName: string;
  column: string;
  filter: IFilter;
}

const SearchContainer: React.FC<ISearchContainer> = ({
  tableName,
  column,
  filter,
  clearInput,
  inputType,
  input,
  handleOnChange,
  styles,
}) =>
  inputType !== 'dateInputs' ? (
    <TextFieldInput styles={styles} clearInput={clearInput} input={input} handleOnChange={handleOnChange} />
  ) : (
    <DateInput styles={styles} tableName={tableName} column={column} filter={filter} />
  );

export default SearchContainer;

interface IDateInputProps {
  filter: IFilter;
  tableName: string;
  column: string;
  styles: Record<string, any>;
}

const DateInput: React.FC<IDateInputProps> = ({ styles, filter, tableName, column }) => {
  const { resetPage } = useFiltersContext();

  useEffect(() => {
    resetPage(tableName, column);
  }, [filter.dateInput]);

  return (
    <Box className={styles.dateInputContainer}>
      <DateField rangeLimit="from" filter={filter} styles={styles} column={column} tableName={tableName} />

      <DateField rangeLimit="to" filter={filter} styles={styles} column={column} tableName={tableName} />
    </Box>
  );
};

interface IDateField {
  filter: IFilter;
  rangeLimit: keyof IDateInput;
  styles: Record<string, any>;
  tableName: string;
  column: string;
}
const DateField: React.FC<IDateField> = ({ rangeLimit, filter, styles, tableName, column }) => {
  const { setInputDate } = useFiltersContext();
  const { t } = useTranslation();

  return (
    <Box mt={1.5}>
      <Grid container spacing={1}>
        <Grid item xs={2}>
          <Box display={'flex'} alignItems={'center'} justifyContent={'center'} style={{ height: '100%' }}>
            <Typography variant="body2">{t(tPaths.table.fromTo(rangeLimit === 'from'))}</Typography>
          </Box>
        </Grid>
        <Grid item xs={10} className={styles.dateInput}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              format="yyyy-MM-dd"
              value={filter.dateInput[rangeLimit]}
              onChange={(e, date) => {
                setInputDate(tableName, column, { [rangeLimit]: date || filter.dateInput[rangeLimit] });
              }}
              disableToolbar
              variant="inline"
              margin="none"
              id="date-picker-inline"
              name={rangeLimit}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              inputProps={{
                style: { width: 170 },
                readOnly: true,
              }}
            />
          </MuiPickersUtilsProvider>
        </Grid>
      </Grid>
    </Box>
  );
};

interface ITextFieldInput {
  input: string;
  handleOnChange: (event: any) => void;
  clearInput: () => void;
  styles: Record<string, any>;
}

const TextFieldInput: React.FC<ITextFieldInput> = ({ styles, input, handleOnChange, clearInput }) => {
  const Icon = () =>
    input.trim().length ? (
      <CancelIcon style={{ cursor: 'pointer', fontSize: 35 }} onClick={clearInput} />
    ) : (
      <Search style={{ cursor: 'pointer', fontSize: 35 }} />
    );

  return (
    <Grid className={styles.search}>
      <TextField
        placeholder="Search"
        size="small"
        variant="standard"
        className={styles.searchInput}
        value={input}
        onChange={handleOnChange}
      />
      <Icon />
    </Grid>
  );
};
